import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { useInFlightIds } from '@mdvip/shared/hooks/useInFlightIds';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { MDVIP_COMPANY_INFO } from '@mdvip/shared/lib/mdvip-company-info';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { type UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PaymentReceiptDownloadError, type ReceiptDownloadResult, requestDownloadPaymentReceipt } from './receiptsApi';

interface UseDownloadPaymentReceiptOptions {
  onSuccess?: (result: ReceiptDownloadResult) => void;
}

/**
 * Fetches one past payment's receipt PDF for download.
 *
 * `useMutation` despite the underlying GraphQL operation being a `query`: it is fired imperatively
 * from a menu click and its result is consumed once, so there is no caching value — and this way it
 * shares the same error-toast pathway as the email operation.
 *
 * Handing the bytes to the browser is the caller's job (see `useGenerateReceipt`), which keeps this
 * hook free of DOM side effects.
 *
 * The explicit `UseMutationResult` annotation is required: `onError` refers back to `mutation` to
 * implement Retry, which TypeScript cannot infer through.
 */
export function useDownloadPaymentReceipt({ onSuccess }: UseDownloadPaymentReceiptOptions = {}) {
  const { toast } = useToast();
  const { t } = useTranslation('members');
  const tError = useErrorTranslation();
  const inFlight = useInFlightIds();

  const mutation: UseMutationResult<ReceiptDownloadResult, unknown, string> = useMutation({
    mutationFn: (paymentId: string) => requestDownloadPaymentReceipt(paymentId),
    // Every invocation passes through here, including a Retry fired from the error toast.
    onMutate: (paymentId: string) => {
      inFlight.add(paymentId);
    },
    onSettled: (_result, _error, paymentId: string) => {
      inFlight.remove(paymentId);
    },
    onSuccess,
    onError: (error: unknown, paymentId: string) => {
      // Same split as the email operation: raw wire code for telemetry, prefixed key for i18n.
      const isDomainError = error instanceof PaymentReceiptDownloadError;
      const code = isDomainError ? error.code : extractGqlErrorCode(error);
      const errorKey = isDomainError ? `PAYMENT_RECEIPT_${code}` : code;
      if (error instanceof Error) {
        trackException(error, { source: 'downloadPaymentReceipt', errorCode: code });
      }
      toast({
        title: t('payments.transactions.receipt.errorTitle'),
        description: tError(errorKey, { phone: MDVIP_COMPANY_INFO.memberSupportPhone }),
        variant: 'destructive',
        // Infinite duration needs an explicit way out, so the dismiss button is not optional here.
        duration: Number.POSITIVE_INFINITY,
        closeButton: true,
        action: { label: t('payments.transactions.receipt.retry'), onClick: () => mutation.mutate(paymentId) },
      });
    },
  });

  // `pendingIds` rather than the mutation's own `isPending`: that boolean only describes the most
  // recent call, so it cannot answer which rows are busy once two are running.
  return { submit: (paymentId: string) => mutation.mutate(paymentId), pendingIds: inFlight.ids };
}
